import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import SubpageLayout from "@/components/SubpageLayout";
import { P } from "@/components/Prose";
import { CATEGORIES } from "@/pages/ressourcer";

const RessourceKategori = () => {
  const { slug } = useParams();
  const category = CATEGORIES.find((c) => c.slug === slug);

  if (!category) {
    return (
      <SubpageLayout title="Siden blev ikke fundet" testId="page-resource-missing">
        <Link
          to="/ressourcer"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors hover:text-[#B46A45]"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbage til Ressourcer
        </Link>
      </SubpageLayout>
    );
  }

  return (
    <SubpageLayout overline="RESSOURCER" title={category.title} testId={`page-resource-${slug}`}>
      <P>{category.text}</P>
      {category.items && category.items.length > 0 ? (
        <div className="mt-6">
          {[...category.items]
            .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
            .map((item, i) => {
              if (item.kind === "text") {
                return (
                  <article
                    key={item.title}
                    data-testid={`resource-item-${slug}-${i}`}
                    className="relative mb-14 mt-6 overflow-hidden rounded-[4px] bg-[#3F5A47] px-7 py-12 last:mb-0 sm:px-12 sm:py-16"
                  >
                    <img
                      src="/brand/vision-dandelion.png"
                      alt=""
                      className="pointer-events-none absolute -right-6 -top-4 w-32 opacity-25 sm:-right-4 sm:w-44"
                    />
                    <p className="relative text-xs font-medium uppercase tracking-[0.2em] text-[#D6C2A6]">
                      {item.meta}
                    </p>
                    <h2 className="relative mt-4 font-serif text-3xl leading-snug text-[#FAF7F1] sm:text-4xl">
                      {item.title}
                    </h2>
                    <div className="relative mt-9 space-y-6">
                      {item.description.split("\n\n").map((para) => (
                        <p
                          key={para.slice(0, 30)}
                          className="whitespace-pre-line font-serif text-lg leading-loose text-[#FAF7F1]/90 sm:text-xl"
                        >
                          {para}
                        </p>
                      ))}
                    </div>
                    {item.byline && (
                      <p className="relative mt-10 text-sm italic text-[#D6C2A6]">{item.byline}</p>
                    )}
                  </article>
                );
              }
              const Wrapper = item.url && !item.links ? "a" : "article";
              return (
              <Wrapper
                key={item.title}
                {...(item.url && !item.links ? { href: item.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={`resource-item-${slug}-${i}`}
                className="group block border-b border-[#D6C2A6]/70 py-12 first:mt-4 first:border-t"
              >
              {item.image && (
                <div className="mb-8">
                  <img
                    src={item.image}
                    alt={item.caption || ""}
                    className={
                      item.imageSize === "small"
                        ? "w-44 rounded-[4px] object-cover sm:w-52"
                        : "max-h-[520px] w-full rounded-[4px] object-cover object-top"
                    }
                  />
                  {item.caption && (
                    <p className="mt-3 text-xs tracking-wide text-[#2F3338]/55">{item.caption}</p>
                  )}
                </div>
              )}
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#B46A45]">
                {item.meta}
              </p>
              <h2 className="mt-3 font-serif text-2xl leading-snug text-[#3F5A47] transition-colors duration-300 group-hover:text-[#B46A45] sm:text-3xl">
                {item.title}
              </h2>
              {item.description && (
                <div className="mt-5 space-y-5">
                  {item.description.split("\n\n").map((para) => (
                    <p key={para.slice(0, 30)} className="text-base leading-loose text-[#2F3338]/80">
                      {para}
                    </p>
                  ))}
                </div>
              )}
                  {item.authors && item.authors.length > 0 && (
                    <div className="mt-7 rounded-[4px] border border-[#D6C2A6]/50 bg-[#D6C2A6]/10 px-5 py-4">
                      <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#B46A45]">
                        Forfattere
                      </p>
                      <ul className="mt-2.5 space-y-1">
                        {item.authors.map((a) => (
                          <li key={a.name} className="text-xs leading-relaxed text-[#2F3338]/75">
                            <span className="font-medium text-[#2F3338]">{a.name}</span>
                            {a.affiliation && (
                              <span className="text-[#2F3338]/55"> · {a.affiliation}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {item.url && (
                    <span className="mt-6 flex flex-wrap items-center gap-x-7 gap-y-3">
                      {(item.links || [{ label: item.linkLabel || "Læs mere", url: item.url }]).map(
                        (l) =>
                          item.links ? (
                            <a
                              key={l.url}
                              href={l.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`resource-item-${slug}-${i}-link`}
                              className="inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors duration-300 hover:text-[#B46A45]"
                            >
                              {l.label}
                              <ArrowUpRight className="h-4 w-4" />
                            </a>
                          ) : (
                            <span
                              key={l.url}
                              className="inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors duration-300 group-hover:text-[#B46A45]"
                            >
                              {l.label}
                              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                            </span>
                          )
                      )}
                    </span>
                  )}
              </Wrapper>
              );
            })}
        </div>
      ) : (
        <P className="mt-8 text-[#2F3338]/60">Indholdet til denne side er på vej.</P>
      )}
      <p className="mt-12">
        <Link
          to="/ressourcer"
          data-testid="resource-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors hover:text-[#B46A45]"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbage til Ressourcer
        </Link>
      </p>
    </SubpageLayout>
  );
};

export default RessourceKategori;
